import { useEffect, useState } from 'react';
import { Card, Button, Form, Toast, ToastContainer, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { API_BASE } from 'apiConfig';

const normalize = (raw: string) =>
  raw.trim().toLowerCase().replace(/^https?:\/\//, '').split('/')[0];

const AllowedDomains = () => {
  const token = useSelector((state: RootState) => state.auth.token);
  const [domains, setDomains] = useState<string[]>([]);
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  useEffect(() => {
    fetch(`${API_BASE}/account/widget-settings`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined
    })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (Array.isArray(d?.allowed_domains)) setDomains(d.allowed_domains);
      })
      .catch(() => undefined);
  }, [token]);

  const add = () => {
    const v = normalize(input);
    if (!v) return;
    if (!domains.includes(v)) setDomains([...domains, v]);
    setInput('');
  };

  const remove = (d: string) => setDomains(domains.filter(x => x !== d));

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/account/allowed-domains`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ domains })
      });
      setToast(
        res.ok ? { msg: 'Domains saved.', ok: true } : { msg: 'Could not save.', ok: false }
      );
    } catch {
      setToast({ msg: 'Could not save.', ok: false });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <h2 className="mb-1 text-body-emphasis">Allowed domains</h2>
      <p className="text-body-tertiary mb-4">
        Restrict your widget to specific websites. When set, the widget will only load on these
        domains. Leave empty to allow it everywhere.
      </p>

      <Card style={{ maxWidth: 640 }}>
        <Card.Body>
          <div className="d-flex gap-2 mb-3">
            <Form.Control
              placeholder="example.com"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  add();
                }
              }}
            />
            <Button variant="phoenix-secondary" onClick={add}>
              <FontAwesomeIcon icon={faPlus} className="me-1" /> Add
            </Button>
          </div>

          {domains.length === 0 ? (
            <p className="fs-9 text-body-tertiary">
              No domains added — the widget loads on any site that uses your license.
            </p>
          ) : (
            <div className="d-flex flex-wrap gap-2 mb-3">
              {domains.map(d => (
                <Badge
                  key={d}
                  bg="secondary"
                  className="d-inline-flex align-items-center gap-2 py-2 px-3 fs-9"
                >
                  {d}
                  <FontAwesomeIcon
                    icon={faXmark}
                    style={{ cursor: 'pointer' }}
                    onClick={() => remove(d)}
                  />
                </Badge>
              ))}
            </div>
          )}

          <Button variant="primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save domains'}
          </Button>
        </Card.Body>
      </Card>

      <ToastContainer position="bottom-end" className="p-3">
        <Toast
          show={!!toast}
          onClose={() => setToast(null)}
          delay={2500}
          autohide
          bg={toast?.ok ? 'success' : 'danger'}
        >
          <Toast.Body className="text-white">{toast?.msg}</Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};

export default AllowedDomains;
